import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };
const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const BANK_CODES = [
  { code: 'BCA', name: 'BCA' }, { code: 'BNI', name: 'BNI' }, { code: 'BRI', name: 'BRI' },
  { code: 'MANDIRI', name: 'Mandiri' }, { code: 'CIMB', name: 'CIMB Niaga' },
  { code: 'PERMATA', name: 'Permata' }, { code: 'DANAMON', name: 'Danamon' },
  { code: 'MAYBANK', name: 'Maybank' }, { code: 'OCBC', name: 'OCBC NISP' },
  { code: 'PANIN', name: 'Panin' }, { code: 'UOB', name: 'UOB' },
  { code: 'HSBC', name: 'HSBC' }, { code: 'CITIBANK', name: 'Citibank' },
  { code: 'DBS', name: 'DBS' }, { code: 'STANDARD_CHARTERED', name: 'Standard Chartered' },
];

const TestingPayment = () => {
  const { profile } = useAuth();
  const [loading, setLoading]             = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [result, setResult]               = useState(null);
  const [error, setError]                 = useState(null);
  const [config, setConfig]               = useState({ apiKey: '', environment: 'sandbox', apiUrl: 'https://api.xendit.co' });
  const [payoutData, setPayoutData]       = useState({ accountNumber: '', bankCode: '', amount: '', notes: '', beneficiaryName: '' });

  useEffect(() => { loadXenditConfig(); }, []);

  const loadXenditConfig = async () => {
    try {
      setLoadingConfig(true);
      const settings = await api.get('/api/settings');
      setConfig({
        environment: settings.find(s => s.key === 'xendit_environment')?.value || 'sandbox',
        apiKey:      settings.find(s => s.key === 'xendit_api_key')?.value      || '',
        apiUrl:      'https://api.xendit.co',
      });
    } catch { /* use defaults */ }
    finally { setLoadingConfig(false); }
  };

  const handlePayoutChange = (e) => setPayoutData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleTestPayout = async (e) => {
    e.preventDefault();
    if (!config.apiKey) { setError('API Key belum dikonfigurasi. Silakan konfigurasi di Settings terlebih dahulu.'); return; }
    if (!payoutData.accountNumber || !payoutData.bankCode || !payoutData.amount) { setError('Account Number, Bank Code, dan Amount wajib diisi.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const response = await api.post('/api/testing/payment', {
        payout: {
          account_number: payoutData.accountNumber,
          bank_code:      payoutData.bankCode,
          amount:         parseInt(payoutData.amount),
          notes:          payoutData.notes || 'Testing payment from Sinii Tracker',
          beneficiary_name: payoutData.beneficiaryName || '',
        },
      });
      setResult(response);
      if (response.success) { setError(null); alert('Payout berhasil dikirim!'); }
      else { setError(response.error || 'Terjadi kesalahan dari Xendit'); }
    } catch (err) {
      const errRes = err.response?.data || err.response;
      if (errRes) { setResult(errRes); setError(errRes.error || err.message); }
      else { setError(err.message || 'Terjadi kesalahan saat mengirim payout'); }
    } finally { setLoading(false); }
  };

  if (profile?.role !== 'admin') {
    return (
      <div className="bg-white rounded-2xl p-12 text-center" style={card}>
        <p className="text-[15px] font-semibold text-[#c62828]">Akses Ditolak</p>
        <p className="text-[13px] text-[#8a8a8a] mt-1">Hanya admin yang dapat mengakses halaman ini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full min-w-0 max-w-2xl">
      <div>
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Testing Payment</h1>
        <p className="text-[13px] text-[#8a8a8a] mt-0.5">Test pembayaran hunter menggunakan Xendit API.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
        <p className="text-[13px] text-amber-700">
          <strong>Peringatan:</strong> Halaman ini hanya untuk testing. Pastikan menggunakan environment sandbox untuk testing awal.
          Konfigurasi Xendit diambil dari menu Settings.
        </p>
      </div>

      {loadingConfig ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Xendit config info */}
          <div className="bg-white rounded-2xl p-5" style={card}>
            <h2 className="text-[14px] font-semibold text-[#3b3b3b] mb-3">Konfigurasi Xendit</h2>
            <div className="space-y-2">
              {[
                ['Environment', <span className="capitalize">{config.environment}</span>],
                ['API Key', config.apiKey ? `${config.apiKey.substring(0, 10)}...` : <span className="text-[#c62828]">Belum dikonfigurasi</span>],
                ['API URL', config.apiUrl],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between py-2 border-b border-[#e5e5e5] last:border-0">
                  <span className="text-[12px] font-medium text-[#646464] flex-shrink-0">{label}:</span>
                  <span className="text-[12px] text-[#3b3b3b] min-w-0 break-all sm:text-right">{value}</span>
                </div>
              ))}
            </div>
            {!config.apiKey && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <p className="text-[12px] text-[#c62828] font-medium">Konfigurasi belum lengkap! Silakan konfigurasi di menu Settings.</p>
              </div>
            )}
          </div>

          {/* Payout form */}
          <div className="bg-white rounded-2xl p-5" style={card}>
            <h2 className="text-[14px] font-semibold text-[#3b3b3b] mb-4">Data Payout</h2>
            <form onSubmit={handleTestPayout} className="space-y-4">
              <div>
                <label className={LABEL}>Nomor Rekening *</label>
                <input type="text" name="accountNumber" value={payoutData.accountNumber} onChange={handlePayoutChange}
                  placeholder="1234567890" className={INPUT} required />
              </div>
              <div>
                <label className={LABEL}>Bank Code *</label>
                <select name="bankCode" value={payoutData.bankCode} onChange={handlePayoutChange} className={INPUT} required>
                  <option value="">Pilih Bank</option>
                  {BANK_CODES.map(b => <option key={b.code} value={b.code}>{b.name} ({b.code})</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>Nama Penerima</label>
                <input type="text" name="beneficiaryName" value={payoutData.beneficiaryName} onChange={handlePayoutChange}
                  placeholder="Nama lengkap penerima" className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Jumlah (IDR) *</label>
                <input type="number" name="amount" value={payoutData.amount} onChange={handlePayoutChange}
                  placeholder="10000" min="10000" className={INPUT} required />
                <p className="text-[11px] text-[#8a8a8a] mt-1">Minimum: Rp 10.000</p>
              </div>
              <div>
                <label className={LABEL}>Catatan</label>
                <textarea name="notes" value={payoutData.notes} onChange={handlePayoutChange}
                  rows={3} placeholder="Catatan untuk payout" className={INPUT + ' resize-none'} />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
                {loading ? 'Mengirim Payout...' : 'Kirim Test Payout'}
              </button>
            </form>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
              <p className="text-[14px] font-semibold text-[#c62828] mb-1">Error</p>
              <p className="text-[13px] text-[#c62828] mb-3">{error}</p>
              {result && (
                <>
                  <p className="text-[12px] font-medium text-[#c62828] mb-1">Response dari Xendit:</p>
                  <pre className="text-[11px] text-[#646464] bg-white p-3 rounded-xl overflow-auto border border-red-200 max-h-64">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </>
              )}
            </div>
          )}

          {/* Success */}
          {result && !error && (
            <div className={`border rounded-2xl p-5 ${result.success ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-[14px] font-semibold mb-2 ${result.success ? 'text-green-700' : 'text-amber-700'}`}>
                {result.success ? 'Payout Berhasil' : 'Response dari Xendit'}
              </p>
              {result.message && <p className="text-[13px] text-[#646464] mb-3">{result.message}</p>}
              <pre className="text-[11px] text-[#646464] bg-white p-3 rounded-xl overflow-auto border border-[#e5e5e5] max-h-64">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TestingPayment;
